import {
  CHESTNY_ZNAK_LABELS_RU,
  CUSTOMS_SOURCE_LABELS_RU,
  CUSTOMS_STATUS_LABELS_RU,
  RAISED_CUSTOMS_FEE_RUB,
  formatDutyRate,
  resolveCustomsZnak,
  type CustomsQueueRow,
  type ResolvedZnak,
} from '@crm/shared'

/**
 * Строка грида очереди нетарифки. Текстовые поля — то, по чему работает фильтр
 * колонки (`CheckboxSetFilter` берёт `data[field]`); `raw` — строка API для
 * рендереров и действий. Где фильтру нужен короткий набор значений, а копированию
 * — полный текст, полный текст лежит отдельным полем (`znakText`).
 */
export interface CustomsGridRow {
  id: string
  requestLabel: string
  managerName: string
  /** «Прислал»: когда и кто отправил позицию логисту — «17.10.2026 10:42 · Иванов». */
  sentBy: string
  partNumber: string
  brand: string
  category: string
  quantity: number
  matrix: string
  datasheet: string
  statusLabel: string
  tnved: string
  duty: string
  fee: string
  /** Для фильтра — ровно три значения: «Нужен» / «Не нужен» / «Неизвестно». */
  znak: string
  /** Для копирования и подсказки: «Нужен с 01.05.2026». */
  znakText: string
  nonTariffNote: string
  certificateNote: string
  featuresNote: string
  attention: string
  checkedAt: string
  source: string
  brokerFileCode: string
  commentsCount: number
  raw: CustomsQueueRow
}

export function formatRuDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU')
}

function sentByText(r: CustomsQueueRow): string {
  const at = r.check?.sentToLogistAt
  if (!at) return ''
  const d = new Date(at)
  const when = `${d.toLocaleDateString('ru-RU')} ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
  const who = r.check?.sentToLogistBy?.name
  return who ? `${when} · ${who}` : when
}

function formatRub(n: number): string {
  return Math.round(n).toLocaleString('ru-RU') + ' ₽'
}

/** ТН ВЭД позиции: своя проверка, иначе доверенная память по партномеру. */
export function tnvedOf(r: CustomsQueueRow): { code: string; fromArchive: boolean } | null {
  if (r.check?.tnvedCode) return { code: r.check.tnvedCode, fromArchive: false }
  if (r.hint?.tnvedCode) return { code: r.hint.tnvedCode, fromArchive: true }
  return null
}

/** «Честный знак» позиции — общее правило shared: своё «Неизвестно» память не перебивает. */
export function znakOf(r: CustomsQueueRow): ResolvedZnak | null {
  return resolveCustomsZnak(r.check, r.hint)
}

/** «Нужен с 01.05.2026» / «Не нужен» / «Неизвестно». */
export function znakText(z: ResolvedZnak): string {
  const from = z.value === 'YES' && z.from ? ` с ${formatRuDate(z.from)}` : ''
  return CHESTNY_ZNAK_LABELS_RU[z.value] + from
}

function feeText(r: CustomsQueueRow): string {
  const raised = r.check?.customsFeeRaised
  if (raised === true)
    return `Повышенный ${formatRub(r.check?.customsFeeAmount || RAISED_CUSTOMS_FEE_RUB)}`
  if (raised === false) return '0'
  // Своего ответа нет — сбор прошлой проверки партномера (серым, «из архива»).
  const last = r.hint?.lastFeeRaised
  if (raised == null && last === true)
    return `Повышенный ${formatRub(r.hint?.lastFeeAmount || RAISED_CUSTOMS_FEE_RUB)}`
  if (raised == null && last === false) return '0'
  return ''
}

export function toCustomsGridRow(r: CustomsQueueRow): CustomsGridRow {
  const c = r.check
  const tnved = tnvedOf(r)
  const znak = znakOf(r)
  const ownDuty = c?.dutyRate != null ? formatDutyRate(c.dutyRate) : ''
  return {
    id: r.itemId,
    requestLabel: [r.requestNumber ?? '—', r.clientCode].filter(Boolean).join(' · '),
    managerName: r.managerName ?? '',
    sentBy: sentByText(r),
    partNumber: r.partNumber,
    brand: r.brand ?? '',
    category: r.category ?? '',
    quantity: r.quantity,
    matrix: r.matrix ?? '',
    // Фильтр — «есть / пусто»; копирование берёт саму ссылку (valueFormatter колонки).
    datasheet: r.datasheetUrl ? 'Есть' : '',
    statusLabel: CUSTOMS_STATUS_LABELS_RU[r.status] ?? r.status,
    tnved: tnved?.code ?? '',
    duty: ownDuty || (r.hint?.lastDutyRate != null ? formatDutyRate(r.hint.lastDutyRate) : ''),
    fee: feeText(r),
    // Ничего не известно — тоже «Неизвестно»: в фильтре ровно три значения.
    znak: CHESTNY_ZNAK_LABELS_RU[znak?.value ?? 'UNKNOWN'],
    znakText: znak ? znakText(znak) : '',
    // Своего текста нет — текст прошлой проверки партномера (колонка рисует его серым).
    nonTariffNote: c?.nonTariffNote ?? r.hint?.lastNonTariffNote ?? '',
    certificateNote: c?.certificateNote ?? '',
    featuresNote: c?.featuresNote ?? '',
    attention: c?.attention ? c.attentionReasons.join(', ') || 'Внимание' : '',
    checkedAt: formatRuDate(c?.checkedAt),
    source: c?.source ? CUSTOMS_SOURCE_LABELS_RU[c.source] : '',
    brokerFileCode: c?.brokerRequest?.fileCode ?? '',
    commentsCount: r.commentsCount,
    raw: r,
  }
}
